import {
  useGovernmentLayerData,
  useGovernmentLayerSettings,
} from "@/hooks/useGovernmentLayer"
import { GovernmentLayerUnavailableOfflineError } from "@/lib/governmentLayers/loadGovernmentLayer"
import {
  formatBufferMetres,
  formatLayerNumber,
  groupGovernmentLayerFeatures,
} from "@fieldbook/common/utils"
import { Button } from "@fieldbook/ui/button"
import { NativeSelect, NativeSelectOption } from "@fieldbook/ui/native-select"
import { Switch } from "@fieldbook/ui/switch"
import { createFileRoute } from "@tanstack/react-router"
import { ArrowLeft, Circle, MapPin, RefreshCw } from "lucide-react"
import { useMemo } from "react"

export const Route = createFileRoute(
  "/_private/projects/$id/data/context/government/$layerId",
)({
  component: GovernmentLayerDetail,
})

/**
 * A government reference dataset (soil-landscape, vegetation, IBRA,
 * cadastre) for this project: store it on the device, show it on the project
 * map, and choose how far beyond the project location to search. Lists the map
 * units found in the search area.
 */
function GovernmentLayerDetail() {
  const { id: projectId, layerId } = Route.useParams()
  const navigate = Route.useNavigate()

  const {
    def,
    isSynced,
    isOnMap,
    setSynced,
    setOnMap,
    bufferMetres,
    bufferChoicesMetres,
    setBufferMetres,
  } = useGovernmentLayerSettings(projectId, layerId)
  const {
    data,
    error,
    isLoading,
    isOnline,
    hasLocation,
    boundary,
    refresh,
    isRefreshing,
    refreshError,
  } = useGovernmentLayerData(projectId, layerId)

  const groups = useMemo(
    () =>
      def && data
        ? groupGovernmentLayerFeatures(def, data.features.features, boundary)
        : [],
    [def, data, boundary],
  )

  if (!def) {
    return (
      <div className="text-muted-foreground py-6 text-center text-sm">
        Unknown dataset.
      </div>
    )
  }

  const featureCount = data?.features.features.length ?? 0

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="mb-2 mt-2 space-y-2">
        <button
          onClick={() =>
            navigate({
              to: "/projects/$id/data/context",
              params: { id: projectId },
            })
          }
          className="text-muted-foreground flex items-center gap-1 text-sm focus-visible:outline-0"
        >
          <ArrowLeft size={12} />
          <span>Context</span>
        </button>
        <span className="text-lg font-medium">{def.title}</span>
        {def.description && (
          <p className="text-muted-foreground text-xs">{def.description}</p>
        )}
      </div>

      {/* Config */}
      <div className="border-muted mb-4 flex flex-col gap-3 border-y py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RefreshCw size={16} className="text-muted-foreground" />
            <div className="flex flex-col">
              <span className="text-sm">Sync offline</span>
              <span className="text-muted-foreground text-xs">
                Store this dataset on the device for offline use
              </span>
            </div>
          </div>
          <Switch checked={isSynced} onCheckedChange={setSynced} />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin size={16} className="text-muted-foreground" />
            <div className="flex flex-col">
              <span className="text-sm">Show on map</span>
              <span className="text-muted-foreground text-xs">
                Display these polygons on the project map
              </span>
            </div>
          </div>
          <Switch checked={isOnMap} onCheckedChange={setOnMap} />
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Circle size={16} className="text-muted-foreground" />
            <div className="flex flex-col">
              <span className="text-sm">Buffer distance</span>
              <span className="text-muted-foreground text-xs">
                Include areas this far beyond the project location
              </span>
            </div>
          </div>
          <div className="w-32 shrink-0">
            <NativeSelect
              size="sm"
              aria-label="Buffer distance"
              value={bufferMetres}
              onChange={(event) => setBufferMetres(Number(event.target.value))}
            >
              {bufferChoicesMetres.map((metres) => (
                <NativeSelectOption key={metres} value={metres}>
                  {formatBufferMetres(metres)}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </div>
        </div>
        {isSynced && (
          <div className="border-muted flex items-center justify-between gap-2 border-t pt-3">
            <span className="text-muted-foreground text-xs">
              {data
                ? `Stored ${new Date(data.fetchedAt).toLocaleString()}`
                : isLoading
                  ? "Downloading…"
                  : "Not yet stored on this device"}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={!isOnline || isRefreshing}
              onClick={() => refresh()}
              className="space-x-1"
            >
              <RefreshCw
                size={14}
                className={isRefreshing ? "animate-spin" : undefined}
              />
              <span>Refresh</span>
            </Button>
          </div>
        )}
      </div>

      {data?.isForDifferentArea && (
        <div className="mb-3 rounded border border-amber-500/40 bg-amber-500/10 p-2 text-xs">
          Couldn't download this dataset for the current buffer distance or
          project location, so this is the copy stored earlier for a different
          area. Refresh when you're online to update it.
        </div>
      )}
      {refreshError && (
        <div className="text-destructive mb-3 text-xs">
          Couldn't refresh: {refreshError.message}
        </div>
      )}

      {/* Map units */}
      {!hasLocation ? (
        <div className="text-muted-foreground py-6 text-center text-sm">
          This project has no location to search around.
        </div>
      ) : isLoading ? (
        <div className="text-muted-foreground py-6 text-center text-sm">
          Loading…
        </div>
      ) : !data && error instanceof GovernmentLayerUnavailableOfflineError ? (
        <div className="text-muted-foreground py-6 text-center text-sm">
          This dataset isn't stored on the device. Connect to the internet or
          enable Sync offline.
        </div>
      ) : !data && error ? (
        <div className="text-destructive py-6 text-center text-sm">
          Unable to load this dataset: {error.message}
        </div>
      ) : groups.length === 0 ? (
        <div className="text-muted-foreground py-6 text-center text-sm">
          Nothing from this dataset in the search area.
        </div>
      ) : (
        <div className="flex flex-col">
          <span className="mb-1 text-xs">
            {groups.length === featureCount
              ? `${featureCount} ${featureCount === 1 ? "polygon" : "polygons"}`
              : `${groups.length} map units • ${featureCount} polygons`}
          </span>
          {groups.map((group) => (
            <details
              key={group.key}
              className="border-muted border-t p-2 text-sm"
            >
              <summary className="flex cursor-pointer list-none items-start gap-2">
                <span
                  className="mt-1 size-3 shrink-0 rounded-sm border border-black/20"
                  style={{ backgroundColor: group.color ?? undefined }}
                />
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="font-medium">
                    {group.label ?? group.code ?? "Unnamed"}
                  </span>
                  <span className="text-muted-foreground text-xs">
                    {[
                      group.code && group.code !== group.label
                        ? group.code
                        : null,
                      group.areaInProjectHa !== null
                        ? `${formatLayerNumber(group.areaInProjectHa)} ha in project`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" • ")}
                  </span>
                </div>
              </summary>
              <dl className="mt-2 space-y-0.5 pl-5 text-xs">
                {def.popupFields.map((field) => {
                  const value = group.fieldValues[field.key]
                  if (value === null || value === undefined || value === "")
                    return null
                  return (
                    <div key={field.key} className="flex justify-between gap-2">
                      <dt className="text-muted-foreground">{field.label}</dt>
                      <dd className="text-right">
                        {field.format === "number"
                          ? formatLayerNumber(value)
                          : String(value)}
                      </dd>
                    </div>
                  )
                })}
              </dl>
              {group.description && (
                <p className="text-muted-foreground mt-2 pl-5 text-xs leading-snug">
                  {group.description}
                </p>
              )}
            </details>
          ))}
        </div>
      )}

      <p className="text-muted-foreground mt-4 text-[11px] leading-snug">
        Source: {def.attribution}
        {def.descriptionNote ? ` ${def.descriptionNote}` : ""}
      </p>
    </div>
  )
}
